import clsx from "clsx";
import { FC, useState } from "react";

import { EditableField } from "components/hover-widgets/EditableField";
import { SectionSidebarOptionsMenu } from "components/menus/SectionSidebarOptionsMenu";
import type { SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { ListItem } from "mds/components/ListItem";
import { BookSectionIcon, EyeNotVisibleIcon } from "mds/icons";
import { ComponentEventProperties } from "utils/amplitude";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { onTitleChanged } from "utils/store-utils";
import { sectionHomeUrl } from "utils/urls";

type AdminSectionListItemProps = {
  section: SectionType;
  canEdit: boolean;
  eventPrefix?: string;
  eventProperties?: ComponentEventProperties["eventProperties"];
  optionsMenuActions: {
    onSettings: () => void;
    onDelete: () => void;
    onDuplicate: () => void;
    onCopyTo: (sectionId: string, courseId?: string, termId?: string) => void;
    onMoveTo: (courseId: string, termId: string) => void;
    onExport: () => void;
    onArchive: () => void;
    onUnarchive: () => void;
  };
};

export const AdminSectionListItem: FC<AdminSectionListItemProps> = ({
  section,
  canEdit,
  eventPrefix,
  eventProperties,
  optionsMenuActions,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { isArchived } = useIsArchived();

  return (
    <ListItem
      className="justify-between"
      eventName={`${eventPrefix}Section selected`}
      eventProperties={{ ...eventProperties, sectionTitle: section.title }}
      kind="primary"
      to={sectionHomeUrl(section.id)}
    >
      <div className="body-s flex w-full min-w-12 items-center justify-between gap-2">
        <div
          className={clsx("flex w-full min-w-12 items-center gap-2", {
            "text-black-tint-55": isArchived(section),
          })}
        >
          {isArchived(section) ? <EyeNotVisibleIcon /> : <BookSectionIcon />}

          <EditableField
            className="w-full min-w-12 truncate"
            isEditing={isEditing}
            label={t("glossary.section_title")}
            textSize="s"
            value={section.title}
            onSetIsEditing={setIsEditing}
            onValueChanged={(newTitle) =>
              onTitleChanged("sections", section.id, newTitle, {
                eventName: "Section - Change title",
              })
            }
          />
        </div>

        {canEdit && (
          <SectionSidebarOptionsMenu optionsMenuActions={optionsMenuActions} section={section} />
        )}
      </div>
    </ListItem>
  );
};
